// The flow-badge row payload and its one reader (#984, Epic #956 phase 2b).
//
// WHY THIS IS ITS OWN MODULE, not part of lib/mcp.ts
//
// `rowBadge()` is a pure parser with no Supabase dependency, and it is called
// from render paths (`@/components/flow/FlowBadges`) that unit tests exercise
// with `vi.mock("@/lib/mcp", …)`. Living here keeps it importable without
// dragging the Supabase client (and therefore the whole mcp surface) into a
// component test. `lib/mcp.ts` re-exports both names, so `from "@/lib/mcp"`
// keeps working for callers that are already talking to the verbs.
//
// Every flow list verb (list_briefs / list_orders / list_qa_inspections /
// list_qa_pending / list_shipments) attaches `badge` to each row. It is the
// output of `ops.list_row_badges` (migration 6141), which is itself a join of
// the completeness evaluator (`ops.missing_items`, 6132) and the status-age
// view (`ops.entity_status_age` + `ops.age_level`, 6135).
//
// **The portal never re-derives any of these numbers.** It renders them and, at
// most, COUNTS ROWS that carry them (`countNeedsAttention` in FlowBadges.tsx).
// If a badge looks wrong the fix is a rule row (`ops.completeness_rules`) or a
// threshold (`ops.stage_age_thresholds`) — never a change here.

export interface RowBadge {
  /** Missing items whose completeness rule level is 'required'. */
  missing_required: number;
  /** Missing items whose completeness rule level is 'recommended'. */
  missing_recommended: number;
  /** required + recommended, as the DB reported it. */
  missing_total: number;
  /** The row's current lifecycle value (a qa_inspection's is its `result`). */
  status: string | null;
  /** When it entered that status (audit-derived, created_at fallback). */
  status_since: string | null;
  /** Days in the current status, fractional. Null = unknown, NOT zero. */
  days_in_status: number | null;
  /** ops.age_level's classification of days_in_status against its thresholds. */
  age_level: "ok" | "warn" | "critical";
}

function badgeNumber(v: unknown): number {
  const n = typeof v === "number" ? v : Number(v);
  return Number.isFinite(n) ? n : 0;
}

/**
 * Read a row's flow badge. List rows are open `Record<string, unknown>`s, so
 * this is the one place that narrows `row.badge` — never cast it inline.
 *
 * Returns null for a row with no badge: either the verb could not resolve one
 * (a concurrent soft delete, or a degraded badge lookup — see
 * cc-os-mcp/lib/row-badges.ts) or the row predates the badge. Null is rendered
 * as "no badge", never as a zero, so "unknown" and "nothing missing" stay
 * distinguishable.
 */
export function rowBadge(row: unknown): RowBadge | null {
  if (!row || typeof row !== "object") return null;
  const raw = (row as { badge?: unknown }).badge;
  if (!raw || typeof raw !== "object") return null;
  const b = raw as Record<string, unknown>;
  const level = b.age_level;
  const days = b.days_in_status;
  return {
    missing_required: badgeNumber(b.missing_required),
    missing_recommended: badgeNumber(b.missing_recommended),
    missing_total: badgeNumber(b.missing_total),
    status: typeof b.status === "string" ? b.status : null,
    status_since: typeof b.status_since === "string" ? b.status_since : null,
    days_in_status: days === null || days === undefined ? null : badgeNumber(days),
    // Never pass through a level ops.age_level did not produce.
    age_level: level === "warn" || level === "critical" ? level : "ok",
  };
}
